import { Component, OnInit } from '@angular/core';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GroupDescriptor, CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import { GroupService } from 'src/app/core/service/group.service';
import { NGXLogger } from 'ngx-logger';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { Group, User } from 'src/app/user/model/user';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Router } from '@angular/router';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { UserService } from 'src/app/core/service/user.service';
import { Organization } from 'src/app/core/model/organization';
import { SettingService } from 'src/app/core/service/setting.service';

@Component({
  standalone: false,
  selector: 'app-channel-group-list',
  templateUrl: './channel-group-list.component.html',
  styleUrls: ['./channel-group-list.component.scss']
})
export class ChannelGroupListComponent implements OnInit
{
  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public deleteDialogOpened = false;
  public selectedGroup: Group = null;
  public enableAdvanced = false;
  public count: number;
  public editDataItem: Group;
  public groupId: string;
  public isNew = false;
  public filterText = '';
  public currentUser: User;
  public isCommunityCoordinatorRole = false;
  public isProgramLeadRole = false;
  public isProgramSupportRole = false;
  public currentUserOrganization: Organization;

  constructor(
    private groupService: GroupService,
    private settingService: SettingService,
    private router: Router,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService,
    private authenticationService: AuthenticationService,
    private userService: UserService)
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.loadGridData();
  }

  public ngOnInit(): void
  {
    this.currentUser = this.authenticationService.currentUser;
    if (this.currentUser)
    {
      this.userService.getUserById(this.currentUser.id, false).subscribe({
        next: (user) =>
        {
          this.isCommunityCoordinatorRole = user.role === 'ROLE_COMMUNITY_COORDINATOR';
          this.isProgramLeadRole = user.role === 'ROLE_PROGRAM_LEAD';
          this.isProgramSupportRole = user.role === 'ROLE_PROGRAM_SUPPORT';
          this.currentUserOrganization = user.organization;
        }
      });
    }
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  public addHandler(): void
  {
    this.isNew = true;
    this.editDataItem = new Group();
    if (this.isCommunityCoordinatorRole || this.isProgramLeadRole || this.isProgramSupportRole)
    {
      this.editDataItem.organization = this.currentUserOrganization;
    }
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action
   */
  public deleteAction(confirm: boolean): void
  {
    const groupId = this.selectedGroup.id;

    if (confirm)
    {
      this.groupService.deleteGroup(groupId).subscribe({
        next: () =>
        {
          this.logger.info(`Group with id = ${groupId} deleted.`);
          this.messageHandlerService.displaySuccess('GROUP-DELETED-SUCCESSFULLY');
          this.view.data = this.view.data.filter(e => e.id !== groupId);
          this.view.refreshView();
          this.count = this.view.data.length;
        },
        error: (error) =>
        {
          this.logger.error(error);
          this.messageHandlerService.displayError(error);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.closeDialog();
    this.selectedGroup = null;
  }

  /**
   * Sets the group to delete, and opens the delete group dialog.
   */
  public deleteHandler(id: number): void
  {
    this.groupService.existsGroup(id).subscribe({
      next: (inUseResponse) =>
      {
        // If in use, avoid deletion
        if (inUseResponse)
        {
          this.messageHandlerService.displayWarning('GROUP-IN-USE-CANNOT-DELETE');
        }
        else
        {
          this.groupService.getGroup(id).subscribe({
            next: (data) =>
            {
              this.selectedGroup = data;
              this.openDialog();
            },
            error: (error) =>
            {
              this.logger.error(error);
              this.messageHandlerService.displayError(error);
            }
          });
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.groupService.getGroup(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.view.state.filter = filter;
    this.view.gridData.data = filterBy(this.view.data, filter);
    this.count = this.view.gridData.data.length;
    this.view.refreshView();
  }

  public searchFieldChange(filterText: string): void
  {
    this.view.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.view.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  public afterSaveHandler(group: Group): void
  {
    // Update userCount. This property is needed for the grid to refresh count.
    // eslint-disable-next-line @typescript-eslint/dot-notation
    group['userCount'] = group.users.filter((x: any) => x.enabled && !x.deleted).length;
    // eslint-disable-next-line @typescript-eslint/dot-notation
    group['organizationName'] = group.organization !== null ? group.organization.name : '';
    this.view.addOrUpdateGridRow(group);
  }

  private loadGridData(): void
  {
    this.view.startLoading();
    this.groupService.getGroups(true).subscribe({
      next: (data) =>
      {
        // userCount calculations - can remove later if aggregated in the backend or other solution...
        for (const [, value] of data.entries())
        {
          // eslint-disable-next-line @typescript-eslint/dot-notation
          value['userCount'] = value.users.filter((x: any) => x.enabled && !x.deleted).length;
        }

        data.forEach((d: any) =>
        {
          d.organizationName = d.organization !== null ? d.organization.name : '';
        });

        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'name', dir: 'asc' }]);
      },
      error: (error) =>
      {
        this.logger.error(error);
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }

  private openDialog(): void
  {
    this.deleteDialogOpened = true;
  }

  private closeDialog(): void
  {
    this.deleteDialogOpened = false;
  }
}
